import { valuesAtMessagePath } from "@/shared/components/visualization/messagePath/valuesAtMessagePath";
import type { FileService } from "@/shared/domain/files";
import type { TopicService } from "@/shared/domain/topics";
import { topicReaderOpener } from "@/shared/reader";

import type { ReadFirstMessage } from "./resolveDefaultBindings";

/**
 * A {@link ReadFirstMessage} backed by the real topic readers.
 *
 * The one piece of `resolveDefaultBindings` that touches the network. It is
 * separate so that the resolution rules stay a pure function over fake topic
 * trees, and so the thing that fetches megabytes of MCAP can be swapped for a
 * stub without pretending to resolve anything.
 *
 * This reads on the main thread rather than through the data-server worker:
 * topic readers work on the main thread (the data-server worker is itself only
 * a thin wrapper over them), and one message of one topic is a far smaller read
 * than the panel's own. Reading here makes model import wait on topic data, so
 * every caller needs an abort path and a way to still import the model when the
 * read fails.
 *
 * Each call opens its own reader and disposes it after the read. A reader
 * keeps the files it has opened, and model import reads only one message per
 * topic, so no reader outlives its call.
 *
 * @param services the file and topic services the readers fetch through
 * @returns a reader that decodes a topic's first message and returns the values
 *   at one path in it, or `undefined` if the topic carries no messages
 */
export function makeFirstMessageReader(services: {
  files: FileService;
  topics: TopicService;
}): ReadFirstMessage {
  return async (node, signal) => {
    const openReader = topicReaderOpener({
      fileService: services.files,
      topicService: services.topics,
    });
    const reader = await openReader({ topicId: node.topic.id, signal });
    try {
      for await (const message of reader.iterMessages({
        messagePathsInclude: [node.data.message_path_id],
        signal,
      })) {
        // The first message decides the whole recording's element ordering. See
        // `resolveDefaultBindings` for why that is sound in practice and not
        // guaranteed by any message definition.
        return valuesAtMessagePath(message.data, node.toParts());
      }
      return undefined;
    } finally {
      reader.dispose();
    }
  };
}
